/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Navigate, type RouteObject } from 'react-router-dom';
import { type ComponentType } from 'react';

import { SpaceSubModuleEnum } from '@coze-foundation/space-ui-adapter';

import {
  Develop,
  DevelopV2,
  AgentIDELayout,
  AgentIDE,
  AgentPublishPage,
  AgentAppPublishPage,
  ProjectIDE,
  ProjectIDEPublish,
  Library,
  PluginLayout,
  PluginToolPage,
  PluginPage,
  KnowledgePreview,
  KnowledgeUpload,
  DocumentWorkspace,
  DatabaseDetail,
} from './async-components';

/** Agent / 应用 / 插件 / 知识库等资源详情路由（新版与 v1 共用） */
export const spaceResourceRoutes: RouteObject[] = [
  {
    path: 'bot/:bot_id',
    Component: AgentIDELayout,
    children: [
      {
        index: true,
        Component: AgentIDE,
      },
      {
        path: 'publish',
        children: [
          {
            index: true,
            Component: AgentPublishPage,
            loader: () => ({
              hasSider: false,
              requireBotEditorInit: false,
              pageName: 'publish',
            }),
          },
        ],
      },
      {
        path: 'publish-app',
        children: [
          {
            index: true,
            Component: AgentAppPublishPage,
            loader: () => ({
              hasSider: false,
              requireBotEditorInit: false,
              pageName: 'publish-app',
            }),
          },
        ],
      },
    ],
    loader: () => ({
      hasSider: false,
      showMobileTips: true,
      requireBotEditorInit: true,
      pageName: 'bot',
    }),
  },
  {
    path: 'project-ide/:project_id/publish',
    loader: () => ({
      hasSider: false,
    }),
    Component: ProjectIDEPublish,
  },
  {
    path: 'project-ide/:project_id/*',
    Component: ProjectIDE,
    loader: () => ({
      hasSider: false,
    }),
  },
  {
    path: 'library',
    Component: Library,
    loader: () => ({
      subMenuKey: SpaceSubModuleEnum.LIBRARY,
    }),
  },
  {
    path: 'knowledge',
    children: [
      {
        path: ':dataset_id',
        element: <KnowledgePreview />,
      },
      {
        path: ':dataset_id/upload',
        element: <KnowledgeUpload />,
      },
    ],
    loader: () => ({
      pageModeByQuery: true,
    }),
  },
  {
    path: 'document-workspace',
    element: <DocumentWorkspace />,
    loader: () => ({
      subMenuKey: SpaceSubModuleEnum.DOCUMENT_WORKSPACE,
    }),
  },
  ...(FEATURE_ENABLE_DATABASE
    ? [
        {
          path: 'database',
          children: [
            {
              path: ':table_id',
              element: <DatabaseDetail />,
            },
          ],
          loader: () => ({
            showMobileTips: true,
            pageModeByQuery: true,
          }),
        },
      ]
    : []),
  ...(FEATURE_ENABLE_PLUGIN
    ? [
        {
          path: 'plugin/:plugin_id',
          Component: PluginLayout,
          children: [
            {
              index: true,
              Component: PluginPage,
            },
            {
              path: 'tool/:tool_id',
              children: [
                {
                  index: true,
                  Component: PluginToolPage,
                },
              ],
            },
          ],
        },
      ]
    : []),
];

export const createSpaceIdRoutes = (
  developPage: ComponentType,
): RouteObject[] => [
  {
    index: true,
    element: <Navigate to="develop" replace />,
  },
  {
    path: 'develop',
    Component: developPage,
    loader: () => ({
      subMenuKey: SpaceSubModuleEnum.DEVELOP,
    }),
  },
  {
    path: 'develop-v2',
    element: <Navigate to="../develop" replace />,
  },
  ...spaceResourceRoutes,
];

export { Develop, DevelopV2 };
